import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  CircularProgress,
} from '@mui/material';
import {
  Notifications,
  Security,
  Schedule,
  Camera,
  Storage,
  Backup,
  Restore,
  Save,
  Refresh,
  Warning,
  CheckCircle,
  Error,
  WhatsApp,
  Send,
  School,
} from '@mui/icons-material';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { useTranslation } from '../utils/translations';
import { API_URL } from '../utils/api';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [loadingWaStatus, setLoadingWaStatus] = useState(false);
  const [connectingWa, setConnectingWa] = useState(false);
  const [resettingWa, setResettingWa] = useState(false);
  const { language } = useThemeContext();
  const { t } = useTranslation(language);

  // Load settings from API
  useEffect(() => {
    loadSettings();
    loadWhatsAppStatus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      loadWhatsAppStatus(false);
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  const loadSettings = async () => {
    try {
      setInitialLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token autentikasi tidak ditemukan');
      }

      console.log('📥 Loading settings...');

      const response = await fetch(`${API_URL}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Gagal memuat pengaturan');
      }

      console.log('✅ Settings loaded:', data);
      
      // Convert API data to local state format
      const formattedSettings = {};
      if (data.data && typeof data.data === 'object') {
        Object.entries(data.data).forEach(([category, categorySettings]) => {
          if (Array.isArray(categorySettings)) {
            categorySettings.forEach(setting => {
              formattedSettings[setting.key] = setting.value;
            });
          }
        });
      }

      console.log('📋 Formatted settings:', formattedSettings);
      setSettings(formattedSettings);
    } catch (error) {
      console.error('❌ Load settings error:', error);
      showSnackbar(error.message || 'Gagal memuat pengaturan', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Prepare settings for batch update
      const settingsToUpdate = Object.entries(settings).map(([key, value]) => ({
        key,
        value
      }));

      console.log('💾 Saving settings:', settingsToUpdate);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token autentikasi tidak ditemukan');
      }

      const response = await fetch(`${API_URL}/api/settings/batch`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings: settingsToUpdate })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Gagal menyimpan pengaturan');
      }

      console.log('✅ Settings saved successfully:', data);
      showSnackbar('Pengaturan berhasil disimpan', 'success');
      
      // Reload settings to get updated values
      await loadSettings();
    } catch (error) {
      console.error('❌ Save settings error:', error);
      showSnackbar(error.message || 'Gagal menyimpan pengaturan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    // Konfirmasi sebelum reset
    if (!window.confirm('Apakah Anda yakin ingin mereset semua pengaturan ke nilai default?')) {
      return;
    }

    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token autentikasi tidak ditemukan');
      }

      console.log('🔄 Resetting settings to default...');

      const response = await fetch(`${API_URL}/api/settings/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Gagal mereset pengaturan');
      }

      console.log('✅ Settings reset successfully:', data);

      // Reload settings after reset
      await loadSettings();
      showSnackbar('Pengaturan direset ke nilai default', 'success');
    } catch (error) {
      console.error('❌ Reset settings error:', error);
      showSnackbar(error.message || 'Gagal mereset pengaturan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadWhatsAppStatus = async (showLoading = true) => {
    try {
      if (showLoading) setLoadingWaStatus(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/settings/whatsapp/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Gagal memuat status WhatsApp');
      }
      setWaStatus(data.data);
    } catch (error) {
      console.error('❌ Load WhatsApp status error:', error);
    } finally {
      if (showLoading) setLoadingWaStatus(false);
    }
  };

  const handleResetWhatsAppAuth = async () => {
    if (!window.confirm('Hapus sesi WhatsApp (.wa-auth) di server? Anda harus scan QR lagi setelah ini.')) {
      return;
    }
    try {
      setResettingWa(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token autentikasi tidak ditemukan');

      const response = await fetch(`${API_URL}/api/settings/whatsapp/reset-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Gagal reset sesi WhatsApp');
      }
      setWaStatus(data.data);
      showSnackbar(data.data?.message || 'Sesi WA direset. Klik Connect lalu scan QR.', 'success');
    } catch (error) {
      console.error('❌ Reset WhatsApp auth error:', error);
      showSnackbar(error.message || 'Gagal reset sesi WhatsApp', 'error');
    } finally {
      setResettingWa(false);
    }
  };

  const handleConnectWhatsApp = async () => {
    try {
      setConnectingWa(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token autentikasi tidak ditemukan');

      const response = await fetch(`${API_URL}/api/settings/whatsapp/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Gagal koneksi WhatsApp');
      }
      setWaStatus(data.data);
      showSnackbar('Permintaan koneksi WhatsApp diproses, scan QR jika muncul', 'success');
    } catch (error) {
      console.error('❌ Connect WhatsApp error:', error);
      showSnackbar(error.message || 'Gagal koneksi WhatsApp', 'error');
    } finally {
      setConnectingWa(false);
    }
  };

  const handleSendWhatsAppTest = async () => {
    if (!testPhone.trim()) {
      showSnackbar('Nomor tujuan test WA wajib diisi', 'warning');
      return;
    }

    try {
      setTestingWhatsApp(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token autentikasi tidak ditemukan');
      }

      const response = await fetch(`${API_URL}/api/settings/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: testPhone,
          message: testMessage
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Gagal mengirim test WhatsApp');
      }

      const queueInfo = data?.data?.queue_position ? ` (antrian ke-${data.data.queue_position})` : '';
      showSnackbar(`Test WhatsApp diproses${queueInfo}`, 'success');
    } catch (error) {
      console.error('❌ WhatsApp test error:', error);
      showSnackbar(error.message || 'Gagal test WhatsApp', 'error');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          {t('settings.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleReset}
            disabled={loading}
          >
            {t('settings.reset')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} /> : t('settings.save')}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Nama sekolah */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <School color="primary" />
                <Typography variant="h6">Informasi Sekolah</Typography>
              </Box>
              <TextField
                fullWidth
                label="Nama Sekolah"
                placeholder="Contoh: SD Negeri 12 Mimaarif"
                value={settings.school_name || ''}
                onChange={(e) => handleSettingChange('school_name', e.target.value)}
                helperText="Dipakai di kartu siswa (cetak QR), layar kiosk absensi, dan teks notifikasi WhatsApp. Klik Simpan di atas setelah mengubah."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Notifications color="primary" />
                <Typography variant="h6">Notifikasi</Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.email_notifications || false}
                    onChange={(e) => handleSettingChange('email_notifications', e.target.checked)}
                  />
                }
                label="Notifikasi Email"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.push_notifications || false}
                    onChange={(e) => handleSettingChange('push_notifications', e.target.checked)}
                  />
                }
                label="Notifikasi Push"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.sms_notifications || false}
                    onChange={(e) => handleSettingChange('sms_notifications', e.target.checked)}
                  />
                }
                label="Notifikasi SMS"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Security color="primary" />
                <Typography variant="h6">Keamanan</Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.two_factor_auth || false}
                    onChange={(e) => handleSettingChange('two_factor_auth', e.target.checked)}
                  />
                }
                label="Autentikasi 2 Faktor"
              />
              
              <TextField
                fullWidth
                label="Timeout Sesi (menit)"
                type="number"
                value={settings.session_timeout || 30}
                onChange={(e) => handleSettingChange('session_timeout', parseInt(e.target.value))}
                sx={{ mt: 2 }}
              />
              
              <TextField
                fullWidth
                label="Kadaluarsa Password (hari)"
                type="number"
                value={settings.password_expiry || 90}
                onChange={(e) => handleSettingChange('password_expiry', parseInt(e.target.value))}
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Attendance Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Schedule color="primary" />
                <Typography variant="h6">Pengaturan Absensi</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Jam Mulai Kerja"
                    type="time"
                    value={settings.work_start_time || '08:00'}
                    onChange={(e) => handleSettingChange('work_start_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Jam Selesai Kerja"
                    type="time"
                    value={settings.work_end_time || '17:00'}
                    onChange={(e) => handleSettingChange('work_end_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Toleransi Keterlambatan (menit)"
                type="number"
                value={settings.late_threshold || 15}
                onChange={(e) => handleSettingChange('late_threshold', parseInt(e.target.value))}
                sx={{ mt: 2 }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.overtime_enabled || false}
                    onChange={(e) => handleSettingChange('overtime_enabled', e.target.checked)}
                  />
                }
                label="Aktifkan Lembur"
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Face Recognition Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Camera color="primary" />
                <Typography variant="h6">Pengenalan Wajah</Typography>
              </Box>
              
              <TextField
                fullWidth
                label="Tingkat Kepercayaan Deteksi (0.1 - 1.0)"
                type="number"
                inputProps={{ min: 0.1, max: 1.0, step: 0.1 }}
                value={settings.face_detection_confidence || 0.8}
                onChange={(e) => handleSettingChange('face_detection_confidence', parseFloat(e.target.value))}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Maksimal Foto Wajah per Siswa"
                type="number"
                inputProps={{ min: 3, max: 10 }}
                value={settings.max_face_images || 5}
                onChange={(e) => handleSettingChange('max_face_images', parseInt(e.target.value))}
                sx={{ mb: 2 }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.auto_capture || false}
                    onChange={(e) => handleSettingChange('auto_capture', e.target.checked)}
                  />
                }
                label="Capture Otomatis"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Storage color="primary" />
                <Typography variant="h6">Sistem</Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.auto_backup || false}
                    onChange={(e) => handleSettingChange('auto_backup', e.target.checked)}
                  />
                }
                label="Backup Otomatis"
              />
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Frekuensi Backup</InputLabel>
                <Select
                  value={settings.backup_frequency || 'daily'}
                  label="Frekuensi Backup"
                  onChange={(e) => handleSettingChange('backup_frequency', e.target.value)}
                >
                  <MenuItem value="daily">Harian</MenuItem>
                  <MenuItem value="weekly">Mingguan</MenuItem>
                  <MenuItem value="monthly">Bulanan</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Retensi Data (hari)"
                type="number"
                value={settings.data_retention || 365}
                onChange={(e) => handleSettingChange('data_retention', parseInt(e.target.value))}
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle color="primary" />
                <Typography variant="h6">Status Sistem</Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Database" 
                    secondary="Terhubung dan berfungsi normal"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Face Recognition API" 
                    secondary="Aktif dan responsif"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Backup System" 
                    secondary="Terakhir backup: 2 jam yang lalu"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Warning color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Storage" 
                    secondary="75% terpakai - Perlu monitoring"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* WhatsApp Integration Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WhatsApp color="success" />
                <Typography variant="h6">Integrasi WhatsApp</Typography>
              </Box>

              <TextField
                select
                fullWidth
                label="Provider WhatsApp"
                value={settings.wa_provider || 'baileys'}
                onChange={(e) => handleSettingChange('wa_provider', e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="baileys">Baileys (Gratis, via QR)</MenuItem>
                <MenuItem value="fonnte">Fonnte (Token API)</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="WhatsApp API Token (untuk Fonnte)"
                value={settings.whatsapp_api_token || ''}
                onChange={(e) => handleSettingChange('whatsapp_api_token', e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Isi token jika pakai Fonnte"
              />

              <TextField
                fullWidth
                label="WhatsApp API URL (untuk Fonnte)"
                value={settings.whatsapp_api_url || 'https://api.fonnte.com/send'}
                onChange={(e) => handleSettingChange('whatsapp_api_url', e.target.value)}
                sx={{ mb: 2 }}
                placeholder="https://api.fonnte.com/send"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Delay Min (ms)"
                    type="number"
                    value={settings.wa_queue_delay_min_ms ?? 4000}
                    onChange={(e) => handleSettingChange('wa_queue_delay_min_ms', parseInt(e.target.value, 10) || 4000)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Delay Max (ms)"
                    type="number"
                    value={settings.wa_queue_delay_max_ms ?? 8000}
                    onChange={(e) => handleSettingChange('wa_queue_delay_max_ms', parseInt(e.target.value, 10) || 8000)}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Dedup TTL (jam)"
                type="number"
                value={settings.wa_dedup_ttl_hours ?? 36}
                onChange={(e) => handleSettingChange('wa_dedup_ttl_hours', parseInt(e.target.value, 10) || 36)}
                sx={{ mt: 2 }}
              />

              <TextField
                fullWidth
                label="Retry Maksimal"
                type="number"
                value={settings.wa_retry_max ?? 2}
                onChange={(e) => handleSettingChange('wa_retry_max', parseInt(e.target.value, 10) || 2)}
                sx={{ mt: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.wa_school_hours_only ?? true}
                    onChange={(e) => handleSettingChange('wa_school_hours_only', e.target.checked)}
                  />
                }
                label="Hanya kirim di jam sekolah"
                sx={{ mt: 1 }}
              />

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Jam Mulai WA"
                    type="time"
                    value={settings.wa_school_start || '05:30'}
                    onChange={(e) => handleSettingChange('wa_school_start', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Jam Akhir WA"
                    type="time"
                    value={settings.wa_school_end || '17:30'}
                    onChange={(e) => handleSettingChange('wa_school_end', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.wa_notify_checkout ?? true}
                    onChange={(e) => handleSettingChange('wa_notify_checkout', e.target.checked)}
                  />
                }
                label="Kirim notifikasi saat check-out / pulang"
                sx={{ mt: 1 }}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status Koneksi WhatsApp Web (QR)
              </Typography>

              <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={waStatus?.connected ? 'success' : 'warning'}
                  label={waStatus?.connected ? 'Terhubung' : waStatus?.connecting ? 'Menghubungkan' : 'Belum terhubung'}
                />
                <Chip size="small" label={`Queue: ${waStatus?.queue_size ?? 0}`} />
                <Chip size="small" label={`Provider: ${waStatus?.provider || '-'}`} />
              </Box>

              {waStatus?.jid && (
                <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                  Device: {waStatus.jid}
                </Typography>
              )}

              {waStatus?.last_error && (
                <Typography variant="caption" color="error" display="block" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                  {waStatus.last_error}
                </Typography>
              )}

              {settings.wa_provider === 'baileys' &&
                (String(waStatus?.last_error || '').includes('403') ||
                  String(waStatus?.last_error || '').toLowerCase().includes('forbidden')) && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                  Kode 403 sering berarti IP hosting (cPanel) dibatasi WhatsApp. Coba reset sesi, tunggu 30 menit,
                  Connect sekali. Jika tetap gagal, gunakan provider Fonnte (token API) — lebih stabil di Rumahweb.
                </Typography>
              )}

              {loadingWaStatus ? (
                <CircularProgress size={18} sx={{ mb: 1 }} />
              ) : null}

              {settings.wa_provider === 'baileys' && waStatus?.qr && !waStatus?.connected ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Scan QR ini dari WhatsApp nomor sekolah (Linked Devices).
                  </Typography>
                  <Box
                    component="img"
                    src={waStatus.qr}
                    alt="QR WhatsApp"
                    sx={{ width: 180, height: 180, border: '1px solid #ddd', borderRadius: 1 }}
                  />
                </Box>
              ) : null}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleConnectWhatsApp}
                  disabled={connectingWa || resettingWa || loading}
                >
                  {connectingWa ? 'Memuat koneksi...' : 'Connect / Refresh QR'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleResetWhatsAppAuth}
                  disabled={connectingWa || resettingWa || loading}
                >
                  {resettingWa ? 'Mereset...' : 'Reset sesi WA'}
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Uji Coba Kirim WhatsApp
              </Typography>

              <TextField
                fullWidth
                label="Nomor tujuan test"
                placeholder="contoh: 08123456789"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Pesan test (opsional)"
                multiline
                minRows={2}
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                color="success"
                startIcon={testingWhatsApp ? <CircularProgress size={16} color="inherit" /> : <Send />}
                onClick={handleSendWhatsAppTest}
                disabled={testingWhatsApp || loading}
              >
                {testingWhatsApp ? 'Mengirim...' : 'Tes WA'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
